import { FormGroup } from '@angular/forms';
import { HeaderData } from '../../components/dynamic-header/category/header-data';
import { Banner, ClickAction, ClickActionChoices } from '../../home/models/home.model';
import { ProductDetailConfiguration } from '../../products/components/dynamic-product-details/product-details.model';
import { Brand, Feature, PriceList, Product, ProductDealCountdown, VariantGroup } from '../../products/models/product.model';
import { Announcement, Company } from '../interfaces/store-configuration.interface';
import { Cart, CartItem } from '../services/cart/cart-api.service';
import { SEO } from '../services/seo/seo.service';

/**
 * Type-safe utility functions to create default values for common interfaces
 * This replaces the unsafe `{} as Type` pattern throughout the application
 */


/**
 * Creates a default HTMLElement (null for safety)
 */
export function createDefaultHTMLElement(): HTMLElement | null {
    return null;
}

/**
 * Creates a default FormGroup
 */
export function createDefaultFormGroup(): FormGroup {
    return new FormGroup({});
}

/**
 * Creates a default Record<string, any>
 */
export function createDefaultRecord(): Record<string, any> {
    return {};
}


/**
 * Creates a default Cart object with all required properties
 */
export function createDefaultCart(): Cart {
    return {
        id: '',
        cart_items: [],
        seller_company: {
            id: '',
            name: '',
            account_id: ''
        } as Company,
        buyer_company: {
            id: '',
            name: '',
            account_id: ''
        } as Company,
        total_items: 0,
        gst_amount: 0,
        subtotal: 0,
        discount_amount: 0,
        discount_percentage: 0,
        cart_total: 0,
        total_savings: 0,
        cashback_points: 0,
        is_active: false,
        is_current: false,
        is_archived: false,
        initialized_by_admin: false,
        has_new_version: false,
        tax: null
    };
}

/**
 * Creates a default CartItem object
 */
export function createDefaultCartItem(): CartItem {
    return {
        id: '',
        product: null,
        effective_price: 0,
        quantity: 0,
        parent_cart_item: null,
        price: 0,
        discount_amount: 0,
        discount_percentage: 0,
        total: 0,
        gst: null,
        tax: '',
        is_product_offer_item: false,
        offer_applied: undefined,
        product_discount_offer: undefined,
        featured_images: null,
        cashback_points: 0,
        is_price_changed: false,
        bid_price: 0,
        is_bidding_offer_item: false,
        is_price_or_offer_changed: false
    };
}


/**
 * Creates a default Product object
 */
export function createDefaultProduct(): Product {
    return {
        id: '',
        name: '',
        display_name: '',
        sku: '',
        brand: {
            id: '',
            name: '',
            display_name: '',
            logo: null,
            description: '',
            is_active: false,
            created_at: new Date(),
            updated_at: new Date(),
            created_by: '',
            last_modified_by: '',
            owner: ''
        } as Brand,
        mrp: 0,
        tags: [],
        one_line_description: '',
        description: '',
        short_description: '',
        highlights: [],
        featured_images: [],
        configurations: [],
        categories: [],
        offers: [],
        price_list: {
            other_offers: [],
            recommended_offers: {
                applicable_amount: 0,
                buyer_groups: [],
                sales_channel: [],
                applied_offer_type: null,
                discount_rate: 0,
                discount_type: '',
                max_allowed_quantity: 0,
                max_allowed_inventory: 0,
                maximum_offer_amount_per_transaction: 0,
                minimum_quantity: 0,
                offer_id: '',
                offer_name: '',
                offer_type: null,
                percent_savings: 0,
                savings: 0,
                description: '',
                terms: '',
                start_date: '',
                end_date: ''
            }
        } as PriceList,
        tax: '',
        is_active: false,
        product_type: 'goods',
        variant_group: {
            id: '',
            name: '',
            description: '',
            product_type: '',
            is_active: false,
            primary_attribute: ''
        } as VariantGroup,
        variant_values: [],
        buyer_groups: [],
        sales_channel: [],
        selling_price: 0,
        is_archived: false,
        is_disabled: false,
        is_exposed: false,
        is_visible: false,
        can_order: false,
        is_allow_back_orders: false,
        max_quantity_per_transaction: 0,
        attributes: [],
        downloads: null,
        global_message: '',
        assets: null,
        features: null,
        products_in_box: '',
        questions_and_answers: '',
        seo_detail: {
            meta_title: '',
            meta_description: '',
            keywords: [],
            also_known_as_names: []
        } as SEO,
        shipping_info: '',
        specifications: [],
        reviews: '',
        product_id: '',
        images: [],
        url_path: '',
        in_stock: false,
        variants: [],
        frequently_bought_together_products: {
            count: 0,
            url: ''
        },
        stock: undefined,
        bidding: undefined,
        product_in_box: null,
        deal: undefined,
        weight: 0,
        dimensions: '',
        unit: undefined,
        manufacturer: '',
        country_of_origin: '',
        frequently_bought_together: [],
        sponsored_products: [],
        similar_products: [],
        accessories: []
    };
}

/**
 * Creates a default Banner object
 */
export function createDefaultBanner(): Banner {
    return new Banner(
        [], // images: BannerImage[]
        '', // id: string
        '', // title: string
        [], // buyer_groups: any[]
        new ClickAction('', ClickActionChoices.none, ''), // click_action: ClickAction
        '', // home_content: string
        false, // is_active: boolean
        false, // is_archived: boolean
        false, // is_primary_banner_section: boolean
        [], // sales_channel: any[]
        '', // seller_company: string
        null, // terms_and_conditions: any
        0 // position: number
    );
}

/**
 * Creates a default Feature object
 */
export function createDefaultFeature(): Feature {
    return {
        title: '',
        description: '',
        images: []
    };
}


/**
 * Creates a default HeaderData object
 */
export function createDefaultHeaderData(): HeaderData {
    return {
        logo: {
            target: '',
            logoSrc: '',
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false,
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        searchIcon: {
            target: '',
            searchIconSrc: '',
            isVisibleOnDesktop: false,
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        searchPlaceholder: '',
        accountIcon: {
            target: '',
            accountIconSrc: '',
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false,
            accountTextContent: '',
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        contactIcon: {
            target: '',
            contactIconSrc: '',
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false,
            contactNoTextContent: '',
            contactNo: '',
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        searchIconMobile: {
            target: '',
            isVisibleOnMobile: false,
            searchIconMobileSrc: '',
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        cartIcon: {
            target: '',
            cartIconSrc: '',
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false,
            cartTextContent: '',
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        storeLocationDesktop: {
            target: '',
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false,
            storeLocationDesktopSrc: '',
            'data-click_action_value': '',
            'data-click_action_choice': ''
        },
        menuItems: [],
        menuListVisibleOnDesktop: false,
        menuListVisibleOnMobile: false,
        headerLayout: '',
        categoryMenuName: '',
        categoryMenuImage: '',
        desktopHamburgerMenuName: '',
        isHeaderConfigured: false,
        dynamicCategoryHeaderConfig: {
            layoutType: '',
            dynamicHeaderType: ''
        },
        hamburgerMenuIcon: '',
        mobileHamburgerMenuIcon: '',
        desktopHamburgerMenuIcon: '',
        closeIcon: '',
        menuCollapseIcon: '',
        desktopStyleConfiguration: {
            headerBackgroundColor: '',
            searchBackgroundColor: '',
            searchInputTextColor: '',
            searchInputBorderColor: '',
            menuBackgroundColor: '',
            subMenuBackgroundColor: '',
            menuTextColor: '',
            subMenuTextColor: '',
            iconBackgroundColor: '',
            iconBorderColor: '',
            iconTextColor: '',
            countBackgroundColor: '',
            countBorderColor: '',
            countTextColor: '',
            searchInputBorderRadius: '',
            iconBorderRadius: '',
            countBorderRadius: '',
            menuFontSize: '',
            subMenuFontSize: '',
            menuHoverTextColor: '',
            subMenuHoverTextColor: '',
            menuHoverBackgroundColor: '',
            subMenuHoverBackgroundColor: ''
        },
        mobileStyleConfiguration: {
            headerBackgroundColor: '',
            searchBackgroundColor: '',
            searchInputTextColor: '',
            searchInputBorderColor: '',
            menuBackgroundColor: '',
            subMenuBackgroundColor: '',
            menuTextColor: '',
            subMenuTextColor: '',
            iconBackgroundColor: '',
            iconBorderColor: '',
            iconTextColor: '',
            countBackgroundColor: '',
            countBorderColor: '',
            countTextColor: '',
            searchInputBorderRadius: '',
            iconBorderRadius: '',
            countBorderRadius: '',
            menuFontSize: '',
            subMenuFontSize: '',
            menuHoverTextColor: '',
            subMenuHoverTextColor: '',
            menuHoverBackgroundColor: '',
            subMenuHoverBackgroundColor: ''
        },
        addToCartOnSearch: {
            isVisibleOnMobile: false,
            isVisibleOnDesktop: false
        },
        searchSuggestionListIcon: '',
        searchSuggestionConfiguration: {
            desktop_configuration: {
                show_search_keywords: false,
                show_product_search: false,
                show_category_search_keywords: false,
                show_display_name_search_keywords: false,
                show_seo_detail_search_keywords: false
            },
            mobile_configuration: {
                show_search_keywords: false,
                show_product_search: false,
                show_category_search_keywords: false,
                show_display_name_search_keywords: false,
                show_seo_detail_search_keywords: false
            }
        },
        contactInfoPanel: undefined,
        headerConfigurations: undefined
    };
}


/**
 * Creates a default Announcement object
 */
export function createDefaultAnnouncement(): Announcement {
    return {
        message: '',
        comment: '',
        page: [],
        background_color: '',
        text_color: ''
    };
}

/**
 * Creates a default ProductDealCountdown object
 */
export function createDefaultProductDealCountdown(): ProductDealCountdown {
    return {
        is_active: false,
        start_time: '',
        end_time: '',
        description: '',
        text_color: '',
        badge_message: '',
        background_color: ''
    };
}


/**
 * Creates a default ProductDetailConfiguration object
 */
export function createDefaultProductDetailConfiguration(): ProductDetailConfiguration {
    return {
        id: '',
        sectionId: '',
        sectionType: '',
        sectionSubType: '',
        mainConfiguration: {
            imageUrls: {
                mobile: {},
                desktop: {}
            },
            textContents: {},
            sectionTagsData: []
        },
        styleConfiguration: {},
        advanceConfiguration: {
            elementVisibility: {
                mobile: {},
                desktop: {}
            },
            sectionVisibility: {
                mobile: {},
                desktop: {}
            }
        }
    };
}


/**
 * Generic function to create a default object of any type
 * This is a fallback for types that don't have specific default functions
 */
export function createDefaultObject<T>(): T {
    return {} as T;
}

/**
 * Creates a default object with null values for optional properties
 */
export function createDefaultObjectWithNulls<T>(): T {
    return null as T;
}

/**
 * Creates a default array of any type
 */
export function createDefaultArray<T>(): T[] {
    return [];
}

/**
 * Creates a default object with empty string values
 */
export function createDefaultStringObject(): Record<string, string> {
    return {};
}

/**
 * Creates a default object with boolean false values
 */
export function createDefaultBooleanObject(): Record<string, boolean> {
    return {};
}

/**
 * Creates a default object with number zero values
 */
export function createDefaultNumberObject(): Record<string, number> {
    return {};
}


/**
 * Type guard to check if an object is not empty
 */
export function isNotEmpty<T>(obj: T | null | undefined): obj is T {
    return obj !== null && obj !== undefined && Object.keys(obj as any).length > 0;
}

/**
 * Type guard to check if an array is not empty
 */
export function isArrayNotEmpty<T>(arr: T[] | null | undefined): arr is T[] {
    return Array.isArray(arr) && arr.length > 0;
}

/**
 * Safe fallback function that returns a default value if the input is null/undefined
 */
export function safeFallback<T>(value: T | null | undefined, defaultValue: T): T {
    return value ?? defaultValue;
}

/**
 * Safe fallback function that returns a default value if the input is empty
 */
export function safeFallbackIfEmpty<T>(value: T | null | undefined, defaultValue: T): T {
    if (value === null || value === undefined) {
        return defaultValue;
    }

    if (typeof value === 'object' && Object.keys(value as any).length === 0) {
        return defaultValue;
    }

    return value;
}


/**
 * Creates a default CategoryFilterDesktopStyleConfiguration object
 */
export function createDefaultCategoryFilterDesktopStyleConfiguration(): any {
    return {
        filterCardBorderColor: '#eeeeee',
        filterCategoryNameTextSize: '18px',
        filterCategoryNameTextColor: '#ffffff',
        filterCategoryNameTextTransform: 'uppercase',
        filterCategoryNameTextFontWeight: 'normal',
        filterCardBackground: '#e0e0e0',
        filterCardBorderRadiusTopLeft: '10px',
        filterCardBorderRadiusTopRight: '10px',
        filterCardBorderRadiusBottomRight: '10px',
        filterCardBorderRadiusBottomLeft: '10px',
        filterClearBtnTextSize: '14px',
        filterClearBtnTextColor: '#ff0000',
        filterClearBtnTextFontWeight: 'bold',
        filterChipBorderColor: '#262e2e',
        filterChipBorderRadiusTopLeft: '5px',
        filterChipBorderRadiusTopRight: '5px',
        filterChipBorderRadiusBottomRight: '5px',
        filterChipBorderRadiusBottomLeft: '5px',
        filterChipTextSize: '12px',
        filterChipTextColor: '#000000',
        filterChipBackground: '#ffffff',
        filterChipTextFontWeight: 'normal',
        facetLabelTextSize: '16px',
        facetLabelTextColor: '#333333',
        facetLabelTextFontWeight: '600',
        facetArrowUpBorderColor: '#626464',
        facetArrowDownBorderColor: '#626464',
        facetLabelTextTransform: 'uppercase',
        facetLabelBackgroundColor: '#ffffff',
        facetLabelBorderColor: '#eeeeee',
        facetLabelBorderRadiusTopLeft: '4px',
        facetLabelBorderRadiusTopRight: '4px',
        facetLabelBorderRadiusBottomRight: '4px',
        facetLabelBorderRadiusBottomLeft: '4px',
        filterChipTextTransform: 'capitalize',
        filterOptionTextTransform: 'none',
        filterClearBtnTextTransform: 'uppercase',
        filterOptionTextSize: '14px',
        filterOptionTextColor: '#262e2e',
        filterOptionFontWeight: 'normal',
        filterOptionBackground: '#ffffff',
        checkBoxBackgroundColor: '#eeeeee',
        checkBoxBorderRadiusTopLeft: '3px',
        checkBoxBorderRadiusTopRight: '3px',
        checkBoxBorderRadiusBottomRight: '3px',
        checkBoxBorderRadiusBottomLeft: '3px',
        filterOptionBackgroundActive: '#ffffff',
        filterOptionBorderColorActive: '#262e2e',
        filterCheckBoxBorderColor: '#262e2e',
        filterCheckMarkIconColorActive: '#262e2e'
    };
}

/**
 * Creates a default CategoryFilterMobileStyleConfiguration object
 */
export function createDefaultCategoryFilterMobileStyleConfiguration(): any {
    return {
        filterCardBorderColor: '#eeeeee',
        filterCategoryNameTextSize: '18px',
        filterCategoryNameTextColor: '#ffffff',
        filterCategoryNameTextTransform: 'uppercase',
        filterCategoryNameTextFontWeight: 'normal',
        filterCardBackground: '#ffffff',
        filterCardBorderRadiusTopLeft: '10px',
        filterCardBorderRadiusTopRight: '10px',
        filterCardBorderRadiusBottomRight: '10px',
        filterCardBorderRadiusBottomLeft: '10px',
        filterClearBtnTextSize: '14px',
        filterClearBtnTextColor: '#ff0000',
        filterClearBtnTextFontWeight: 'bold',
        filterChipBorderColor: '#262e2e',
        filterChipBorderRadiusTopLeft: '5px',
        filterChipBorderRadiusTopRight: '5px',
        filterChipBorderRadiusBottomRight: '5px',
        filterChipBorderRadiusBottomLeft: '5px',
        filterChipTextSize: '12px',
        filterChipTextColor: '#000000',
        filterChipBackground: '#ffffff',
        filterChipTextFontWeight: 'normal',
        facetLabelTextSize: '16px',
        facetLabelTextColor: '#333333',
        facetLabelTextFontWeight: '600',
        facetArrowUpBorderColor: '#626464',
        facetArrowDownBorderColor: '#626464',
        facetLabelTextTransform: 'uppercase',
        facetLabelBackgroundColor: '#ffffff',
        facetLabelBorderColor: '#eeeeee',
        facetLabelBorderRadiusTopLeft: '4px',
        facetLabelBorderRadiusTopRight: '4px',
        facetLabelBorderRadiusBottomRight: '4px',
        facetLabelBorderRadiusBottomLeft: '4px',
        filterChipTextTransform: 'capitalize',
        filterOptionTextTransform: 'none',
        filterClearBtnTextTransform: 'uppercase',
        filterOptionTextSize: '14px',
        filterOptionTextColor: '#444444',
        filterOptionFontWeight: 'normal',
        filterOptionBackground: '#ffffff',
        checkBoxBackgroundColor: '#eeeeee',
        checkBoxBorderRadiusTopLeft: '3px',
        checkBoxBorderRadiusTopRight: '3px',
        checkBoxBorderRadiusBottomRight: '3px',
        checkBoxBorderRadiusBottomLeft: '3px',
        filterOptionBackgroundActive: '#ffffff',
        filterOptionBorderColorActive: '#262e2e',
        filterCheckBoxBorderColor: '#262e2e',
        filterCheckMarkIconColorActive: '#262e2e'
    };
}

/**
 * Creates a default CategoryToolDesktopStyleConfiguration object
 */
export function createDefaultCategoryToolDesktopStyleConfiguration(): any {
    return {
        mainCardBackground: '#ffffff',
        mainCardBorderRadiusTopLeft: '8px',
        mainCardBorderRadiusTopRight: '8px',
        mainCardBorderRadiusBottomRight: '8px',
        mainCardBorderRadiusBottomLeft: '8px',
        mainCardBorderColor: '#eeeeee',
        dropdownButtonBackground: '#ffffff',
        dropdownButtonBorderRadiusTopLeft: '4px',
        dropdownButtonBorderRadiusTopRight: '4px',
        dropdownButtonBorderRadiusBottomRight: '4px',
        dropdownButtonBorderRadiusBottomLeft: '4px',
        dropdownButtonBorderColor: '#cccccc',
        dropdownButtonTextSize: '14px',
        dropdownButtonTextColor: '#333333',
        sortArrowDownBorderColor: '#666666',
        dropdownButtonTextFontWeight: 'normal',
        dropdownButtonTextTransform: 'none',
        viewAsTextSize: '14px',
        viewAsTextColor: '#333333',
        viewAsTextFontWeight: 'normal',
        sortByTextSize: '14px',
        sortByTextColor: '#333333',
        sortByTextFontWeight: 'normal',
        sortByTextTransform: 'none',
        viewAsTextTransform: 'none',
        gridTileColor: '#180707',
        activeGridTileColor: '#262e2e'
    };
}

/**
 * Creates a default ToolBarDataMobileStyleConfiguration object
 */
export function createDefaultToolBarDataMobileStyleConfiguration(): any {
    return {
        mainCardBackground: '#ffffff',
        mainCardBorderRadiusTopLeft: '8px',
        mainCardBorderRadiusTopRight: '8px',
        mainCardBorderRadiusBottomRight: '8px',
        mainCardBorderRadiusBottomLeft: '8px',
        mainCardBorderColor: '#eeeeee',
        dropdownButtonBackground: '#ffffff',
        sortArrowDownBorderColor: '#666666',
        dropdownButtonBorderRadiusTopLeft: '4px',
        dropdownButtonBorderRadiusTopRight: '4px',
        dropdownButtonBorderRadiusBottomRight: '4px',
        dropdownButtonBorderRadiusBottomLeft: '4px',
        dropdownButtonBorderColor: '#cccccc',
        dropdownButtonTextSize: '14px',
        dropdownButtonTextColor: '#333333',
        dropdownButtonTextFontWeight: 'normal',
        dropdownButtonTextTransform: 'none',
        viewAsTextSize: '14px',
        viewAsTextColor: '#333333',
        viewAsTextFontWeight: 'normal',
        sortByTextSize: '14px',
        sortByTextColor: '#333333',
        sortByTextFontWeight: 'normal',
        sortByTextTransform: 'none',
        viewAsTextTransform: 'none'
    };
}

/**
 * Creates a default PaginationDataDesktopStyleConfiguration object
 */
export function createDefaultPaginationDataDesktopStyleConfiguration(): any {
    return {
        paginationBtnTextSize: '14px',
        paginationBtnTextColor: '#333333',
        paginationBtnBackground: '#ffffff',
        paginationBtnFontWeight: 'normal',
        paginationBtnTextTransform: 'none',
        paginationBtnBorderColor: '#cccccc',
        paginationBtnBorderRadiusTopLeft: '4px',
        paginationBtnBorderRadiusTopRight: '4px',
        paginationBtnBorderRadiusBottomRight: '4px',
        paginationBtnBorderRadiusBottomLeft: '4px',
        pageCountBtnTextSize: '14px',
        pageCountBtnTextColor: '#333333',
        pageCountBtnBackground: '#f8f9fa',
        pageCountBtnFontWeight: 'normal',
        pageCountBtnTextTransform: 'none',
        pageCountBtnBorderColor: '#cccccc',
        pageCountBorderRadiusTopLeft: '4px',
        pageCountBorderRadiusTopRight: '4px',
        pageCountBorderRadiusBottomRight: '4px',
        pageCountBorderRadiusBottomLeft: '4px'
    };
}

/**
 * Creates a default PaginationDataMobileStyleConfiguration object
 */
export function createDefaultPaginationDataMobileStyleConfiguration(): any {
    return {
        paginationBtnTextSize: '14px',
        paginationBtnTextColor: '#333333',
        paginationBtnBackground: '#ffffff',
        paginationBtnFontWeight: 'normal',
        paginationBtnTextTransform: 'none',
        paginationBtnBorderColor: '#cccccc',
        paginationBtnBorderRadiusTopLeft: '4px',
        paginationBtnBorderRadiusTopRight: '4px',
        paginationBtnBorderRadiusBottomRight: '4px',
        paginationBtnBorderRadiusBottomLeft: '4px',
        pageCountBtnTextSize: '14px',
        pageCountBtnTextColor: '#333333',
        pageCountBtnBackground: '#f8f9fa',
        pageCountBtnFontWeight: 'normal',
        pageCountBtnTextTransform: 'none',
        pageCountBtnBorderColor: '#cccccc',
        pageCountBorderRadiusTopLeft: '4px',
        pageCountBorderRadiusTopRight: '4px',
        pageCountBorderRadiusBottomRight: '4px',
        pageCountBorderRadiusBottomLeft: '4px'
    };
}

/**
 * Creates a default ProductCardDataDesktopStyleConfiguration object
 */
export function createDefaultProductCardDataDesktopStyleConfiguration(): any {
    return {
        productCardBackground: '#ffffff',
        productCardBorderColor: '#eeeeee',
        productCardBorderTopLeftRadius: '8px',
        productCardBorderTopRightRadius: '8px',
        productCardBorderBottomRightRadius: '8px',
        productCardBorderBottomLeftRadius: '8px',
        productNameTextSize: '16px',
        productNameTextColor: '#333333',
        productNameFontWeight: '600',
        productNameTextTransform: 'none',
        brandNameTextSize: '14px',
        brandNameTextColor: '#666666',
        brandNameFontWeight: 'normal',
        brandNameTextTransform: 'none',
        discountedPriceTextSize: '16px',
        discountedPriceTextColor: '#e74c3c',
        discountedPriceFontWeight: '600',
        actualPriceTextSize: '14px',
        percentageDiscountTextColor: '#27ae60',
        percentageDiscountTextSize: '12px',
        percentageDiscountFontWeight: '600',
        actualPriceTextColor: '#999999',
        actualPriceFontWeight: 'normal',
        productCardTagBackground: '#f39c12',
        productCardTagBorderColor: '#f39c12',
        productCardTagTextSize: '12px',
        productCardTagTextColor: '#ffffff',
        productCardTagFontWeight: '600',
        productCardTagTextTransform: 'uppercase',
        productCardTagBorderTopLeftRadius: '4px',
        productCardTagBorderTopRightRadius: '4px',
        productCardTagBorderBottomRightRadius: '4px',
        productCardTagBorderBottomLeftRadius: '4px',
        productCardDescriptionTextSize: '14px',
        productCardDescriptionTextColor: '#666666',
        productCardDescriptionFontWeight: 'normal',
        productCardDescriptionLineHeight: '1.4'
    };
}

/**
 * Creates a default ProductCardDataMobileStyleConfiguration object
 */
export function createDefaultProductCardDataMobileStyleConfiguration(): any {
    return {
        productCardBackground: '#ffffff',
        productCardBorderColor: '#eeeeee',
        productCardBorderTopLeftRadius: '8px',
        productCardBorderTopRightRadius: '8px',
        productCardBorderBottomRightRadius: '8px',
        productCardBorderBottomLeftRadius: '8px',
        productNameTextSize: '14px',
        productNameTextColor: '#333333',
        percentageDiscountTextColor: '#27ae60',
        percentageDiscountTextSize: '12px',
        percentageDiscountFontWeight: '600',
        productNameFontWeight: '600',
        productNameTextTransform: 'none',
        brandNameTextSize: '12px',
        brandNameTextColor: '#666666',
        brandNameFontWeight: 'normal',
        brandNameTextTransform: 'none',
        discountedPriceTextSize: '14px',
        discountedPriceTextColor: '#e74c3c',
        discountedPriceFontWeight: '600',
        actualPriceTextSize: '12px',
        actualPriceTextColor: '#999999',
        actualPriceFontWeight: 'normal',
        productCardTagBackground: '#f39c12',
        productCardTagTextSize: '10px',
        productCardTagBorderColor: '#f39c12',
        productCardTagTextColor: '#ffffff',
        productCardTagFontWeight: '600',
        productCardTagTextTransform: 'uppercase',
        productCardTagBorderTopLeftRadius: '4px',
        productCardTagBorderTopRightRadius: '4px',
        productCardTagBorderBottomRightRadius: '4px',
        productCardTagBorderBottomLeftRadius: '4px',
        productCardDescriptionTextSize: '12px',
        productCardDescriptionTextColor: '#666666',
        productCardDescriptionFontWeight: 'normal',
        productCardDescriptionLineHeight: '1.4'
    };
}

/**
 * Creates a default CategoryLayoutData object
 */
export function createDefaultCategoryLayoutData(): any {
    return {
        categoryLayoutConfig: {
            categoryLayoutType: '',
            themeBackgroundColor: '#ffffff',
            themeFontFamily: 'Inter'
        },
        bannerConfiguration: createDefaultRecord(),
        filterData: {
            mainConfiguration: createDefaultRecord(),
            styleConfiguration: {
                desktopStyleConfiguration: createDefaultCategoryFilterDesktopStyleConfiguration(),
                mobileStyleConfiguration: createDefaultCategoryFilterMobileStyleConfiguration()
            },
            advanceConfiguration: createDefaultRecord()
        },
        toolBarData: {
            mainConfiguration: createDefaultRecord(),
            styleConfiguration: {
                desktopStyleConfiguration: createDefaultCategoryToolDesktopStyleConfiguration(),
                mobileStyleConfiguration: createDefaultToolBarDataMobileStyleConfiguration()
            },
            advanceConfiguration: createDefaultRecord()
        },
        paginationData: {
            mainConfiguration: createDefaultRecord(),
            styleConfiguration: {
                desktopStyleConfiguration: createDefaultPaginationDataDesktopStyleConfiguration(),
                mobileStyleConfiguration: createDefaultPaginationDataMobileStyleConfiguration()
            },
            advanceConfiguration: createDefaultRecord()
        },
        productCardData: {
            mainConfiguration: createDefaultRecord(),
            styleConfiguration: {
                desktopStyleConfiguration: createDefaultProductCardDataDesktopStyleConfiguration(),
                mobileStyleConfiguration: createDefaultProductCardDataMobileStyleConfiguration()
            },
            advanceConfiguration: {
                mobile: {
                    isBrandNameVisible: false,
                    isAddToCartVisible: false,
                    isProductVariantVisible: false,
                    isPreviewDetailsVisible: false,
                    isProductNameVisible: false,
                    isProductPriceVisible: false,
                    isProductImageVisible: false,
                    isPercentageDiscountVisible: false,
                    isDealCountdownVisible: false
                },
                desktop: {
                    isBrandNameVisible: false,
                    isAddToCartVisible: false,
                    isProductVariantVisible: false,
                    isPreviewDetailsVisible: false,
                    isProductNameVisible: false,
                    isProductPriceVisible: false,
                    isProductImageVisible: false,
                    isPercentageDiscountVisible: false,
                    isDealCountdownVisible: false
                },
                isTagVisible: false,
                mobileGridLayout: false,
                isDescriptionVisible: false
            }
        }
    };
}
